import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Skeleton } from '../ui/Skeleton';
import { Button } from '../ui/Button';
import { apiClient } from '../../lib/api-client';
import { usePermission } from '../../hooks/usePermission';

export interface WebhookEventsTabProps {}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleString() : iso;
}

export function WebhookEventsTab(_props: WebhookEventsTabProps) {
  const navigate = useNavigate();
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<Set<string>>(new Set());
  const { isAllowed: canManage } = usePermission('cicd', 'manage_products');

  const load = () => {
    setLoading(true);
    apiClient
      .getCicdWebhookEvents()
      .then((res) => setEvents(res?.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleting((prev) => new Set(prev).add(id));
    try {
      await apiClient.deleteCicdWebhookEvent(id);
      setEvents((prev) => prev.filter((ev) => ev.id !== id));
    } finally {
      setDeleting((prev) => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  const handleRowClick = (ev: any) => {
    // Navigate to the workflows Runs tab and search by the triggeredBy label that
    // all runs from this webhook event share (e.g. "cicd-webhook:pr603").
    // This is present in the parameters JSON of every workflow run.
    const searchTerm = ev.prId != null ? `cicd-webhook:pr${ev.prId}` : (ev.productId || '');
    navigate('/workflows', { state: { openTab: 'runs', runsQuery: searchTerm } });
  };

  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <p className="text-center py-12 text-gray-500 dark:text-gray-400">
        No webhook events yet.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-700">
            <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">Time</th>
            <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">Provider</th>
            <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">Product</th>
            <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">Repository</th>
            <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">PR #</th>
            <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">Action</th>
            <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">Status</th>
            <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">Details</th>
            {canManage && <th className="py-2 px-3" />}
          </tr>
        </thead>
        <tbody>
          {events.map((ev) => (
            <tr
              key={ev.id}
              onClick={() => handleRowClick(ev)}
              className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors"
            >
              <td className="py-2 px-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                {formatDate(ev.timestamp)}
              </td>
              <td className="py-2 px-3">
                <Badge
                  variant="outline"
                  className={
                    ev.provider === 'ado'
                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
                  }
                >
                  {ev.provider === 'ado' ? 'ADO' : ev.provider === 'github' ? 'GitHub' : ev.provider || '—'}
                </Badge>
              </td>
              <td className="py-2 px-3 text-gray-900 dark:text-gray-100">{ev.productId || '—'}</td>
              <td className="py-2 px-3 text-gray-900 dark:text-gray-100 font-mono text-xs">{ev.repoName || '—'}</td>
              <td className="py-2 px-3 text-gray-900 dark:text-gray-100">{ev.prId ?? '—'}</td>
              <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{ev.action || '—'}</td>
              <td className="py-2 px-3">
                <Badge
                  variant="outline"
                  className={
                    ev.status === 'triggered'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : ev.status === 'ignored' || ev.status === 'ping'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                        : ev.status === 'skipped' || ev.status === 'no_targets' || ev.status === 'duplicate'
                          ? 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-500'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
                  }
                >
                  {ev.status || '—'}
                </Badge>
              </td>
              <td className="py-2 px-3 text-gray-600 dark:text-gray-400 max-w-xs">
                {ev.reason ? (
                  <span className="text-xs truncate block" title={ev.reason}>{ev.reason}</span>
                ) : Array.isArray(ev.triggeredWorkflows) && ev.triggeredWorkflows.length > 0 ? (
                  <span className="font-mono text-xs truncate block">{ev.triggeredWorkflows.join(', ')}</span>
                ) : '—'}
              </td>
              {canManage && (
                <td className="py-2 px-3" onClick={(e) => e.stopPropagation()}>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => handleDelete(ev.id, e)}
                    disabled={deleting.has(ev.id)}
                    className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 p-1 h-auto"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
